/**
 * Globally-unique hotspot anchor names.
 *
 * Each value identifies a DOM mount point that an `OnboardingHotspot` or
 * `OnboardingInfoTooltip` can attach to. Targets are shared across flows:
 * multiple onboarding flows may attach hotspots to the same target, but the
 * string itself must be unique.
 *
 * Add new targets here as they're introduced. Group by feature area for
 * discoverability. Keep the right-hand string stable — changing it breaks
 * every consumer that already references it.
 */
export const HOTSPOT_TARGET = {
  // ---- Global nav ----
  CALLS_NAVIGATION_MENU: 'calls-navigation-menu',
  TOP_BAR_SETTINGS_ICON: 'top-bar-settings-icon',

  // ---- Call history ----
  CALL_HISTORY_CALL_INSIGHT_BUTTON: 'call-history-call-insight-button',

  // ---- Call intelligence ----
  CALL_INTEL_OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON: 'call-intel-overview-active-opportunities-info-icon',
  CALL_INTEL_OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON_TOOLTIP_POPOVER:
    'call-intel-overview-active-opportunities-info-icon-tooltip-popover',
  CALL_INTEL_OVERVIEW_SENTIMENT_UNHAPPY_CHART_SEGMENT: 'call-intel-overview-sentiment-unhappy-chart-segment',
  CALL_INTEL_OVERVIEW_SENTIMENT_HAPPY_CHART_SEGMENT: 'call-intel-overview-sentiment-happy-chart-segment',
  CALL_INTEL_CALL_INSIGHT_BUTTON: 'call-intel-call-insight-button',
  CALL_INTEL_FOLLOW_UP_TASKS_TAB: 'call-intel-follow-up-tasks-tab',
  CALL_INTEL_FOLLOW_UP_SCHEDULING_TASKS_LIST_BUTTON: 'call-intel-follow-up-scheduling-tasks-list-button',
  CALL_INTEL_FOLLOW_UP_ALL_FOLLOW_UPS_LIST_BUTTON: 'call-intel-follow-up-all-follow-ups-list-button',
  CALL_INTEL_FOLLOW_UP_TASK_ROW: 'call-intel-follow-up-task-row',

  // ---- Call insight panel ----
  CALL_INSIGHT_PANEL_TRANSCRIPT_TAB: 'call-insight-panel-transcript-tab',
  CALL_INSIGHT_PANEL_CALL_BREAKDOWN: 'call-insight-panel-call-breakdown',
  CALL_INSIGHT_PANEL_MAKE_CORRECTION_BUTTON: 'call-insight-panel-make-correction-button',
  CALL_INSIGHT_PANEL_SCHEDULING_OUTCOME_LABEL: 'call-insight-panel-scheduling-outcome-label',
  CALL_INSIGHT_PANEL_EDIT_AI_VIEW_LABEL: 'call-insight-panel-edit-ai-view-label',
  CALL_INSIGHT_PANEL_FEEDBACK_BUTTON: 'call-insight-panel-feedback-button',
  CALL_INSIGHT_PANEL_NEGATIVE_FEEDBACK_MODAL: 'call-insight-panel-negative-feedback-modal',

  // ---- Patient profile ----
  PATIENT_CONTACT_AVATAR: 'patient-contact-avatar',
  PATIENT_PROFILE_LAST_CALL_CARD: 'patient-profile-last-call-card',
  PATIENT_PROFILE_VIEW_HISTORY_BUTTON: 'patient-profile-view-history-button',
  PATIENT_PROFILE_HISTORY_TAB_CALL: 'patient-profile-history-tab-call',

  // ---- Setting Page: User management ----
  SETTINGS_PAGE_USER_MANAGEMENT_GROUP: 'settings-page-user-management-group',

  // ---- Setting Page: User management ----
  ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON: 'organization-settings-user-table-action-button',
} as const;
